import type { Font } from 'opentype.js';
import { getFace, getFamily } from '@/catalogs/fonts';

/**
 * Загрузка файлов шрифтов с собственного origin.
 *
 * Два независимых механизма:
 *  - opentype.js читает ttf и даёт метрики и контуры (нужно для макета и экспорта);
 *  - CSS Font Loading API подгружает woff2 для интерфейса.
 *
 * Оба файла — одно и то же статическое начертание, поэтому предпросмотр
 * интерфейса и итоговый документ показывают одинаковые буквы.
 */

const parsed = new Map<string, Promise<Font>>();
const cssLoaded = new Set<string>();

/** Базовый путь к ассетам; в тестах подменяется. */
let assetBase = '';

export function setAssetBase(base: string): void {
  assetBase = base;
}

export function faceKey(familyId: string, weight: number): string {
  const face = getFace(familyId, weight);
  return `${familyId}@${face.weight}`;
}

async function parseFont(url: string): Promise<Font> {
  const opentype = await import('opentype.js');
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Не удалось загрузить шрифт: ${url} (${response.status})`);
  const buffer = await response.arrayBuffer();
  return opentype.parse(buffer);
}

/** Возвращает разобранный шрифт; повторные запросы берутся из памяти. */
export function loadOutlineFont(familyId: string, weight: number): Promise<Font> {
  const face = getFace(familyId, weight);
  const key = faceKey(familyId, weight);
  let promise = parsed.get(key);
  if (!promise) {
    promise = parseFont(assetBase + face.ttf).catch((err: unknown) => {
      parsed.delete(key);
      throw err;
    });
    parsed.set(key, promise);
  }
  return promise;
}

/** Уже разобранный шрифт, если он есть в памяти. Синхронно, без сети. */
export function peekOutlineFont(familyId: string, weight: number): Font | undefined {
  return syncCache.get(faceKey(familyId, weight));
}

const syncCache = new Map<string, Font>();

export async function ensureOutlineFont(familyId: string, weight: number): Promise<Font> {
  const font = await loadOutlineFont(familyId, weight);
  syncCache.set(faceKey(familyId, weight), font);
  return font;
}

/**
 * Подгружает woff2 для интерфейса. Экспорт этого не ждёт — он работает
 * с контурами, — но предпросмотр интерфейса должен видеть тот же шрифт.
 */
export async function ensureCssFont(familyId: string, weight: number): Promise<void> {
  const key = faceKey(familyId, weight);
  if (cssLoaded.has(key)) return;
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  const family = getFamily(familyId);
  const face = getFace(familyId, weight);
  await document.fonts.load(`${face.weight} 16px "${family.cssFamily}"`);
  cssLoaded.add(key);
}

/** Только для тестов: сбрасывает кеши. */
export function resetFontCaches(): void {
  parsed.clear();
  syncCache.clear();
  cssLoaded.clear();
}
